import { CallbackWrap } from '@/types/common'
import useApi from 'api'
import { getStoredValue, setStoredValue } from 'common'
import { isArray } from 'lodash'
import React from 'react'
import Session from 'store/session'
import {
  filterArray,
  isNumber,
  isObject,
  isString,
  keyIs
} from 'utils/validation'

export type LevelingSaveStatus =
  | 'local'
  | 'user'
  | 'saving'
  | 'error'
  | 'loaderror'
  | 'loading'

type LevelingProfile = {
  name: string
  completed: Set<string>
}

type LevelingMetadata = {
  profiles: LevelingProfile[]
  active: number
  user: number | undefined
}

const defaultData: Omit<LevelingMetadata, 'user'> = {
  profiles: [
    {
      name: 'Default Profile',
      completed: new Set()
    }
  ],
  active: 0
}

function importData(
  source: unknown,
  user: number | undefined
): LevelingMetadata {
  if (!isObject(source)) return { ...defaultData, user }
  if (isArray(source)) {
    return {
      ...defaultData,
      profiles: [
        {
          ...defaultData.profiles[0],
          completed: new Set(source.filter(isString))
        }
      ],
      user
    }
  }
  if (!keyIs(source, 'profiles', isArray)) return { ...defaultData, user }
  const result: LevelingMetadata = {
    profiles: filterArray(source.profiles, (p, index) =>
      isObject(p)
        ? {
            name: keyIs(p, 'name', isString) ? p.name : `Profile ${index + 1}`,
            completed: new Set(
              keyIs(p, 'completed', isArray) ? p.completed.filter(isString) : []
            )
          }
        : undefined
    ),
    active: 0,
    user
  }
  if (keyIs(source, 'active', isNumber) && result.profiles[source.active]) {
    result.active = source.active
  }
  return result
}

function exportData(data: LevelingMetadata) {
  return {
    profiles: data.profiles.map((p) => ({
      name: p.name,
      completed: [...p.completed]
    })),
    active: data.active
  }
}

export function useLevelingMetadata(metadataKey: string) {
  const [state, setState] = React.useState(() =>
    importData(getStoredValue(metadataKey), undefined)
  )
  const userId = Session.useSelector((state) => state.user?.id)
  const [status, setStatus] = React.useState<LevelingSaveStatus>('local')
  const api = useApi()
  const valueRef = React.useRef(state)

  const timeoutRef = React.useRef<number>()
  React.useEffect(() => {
    if (state !== valueRef.current) {
      const data = exportData(state)
      if (!userId || userId !== state.user) {
        setStoredValue(metadataKey, data)
        setStatus('local')
      } else {
        if (timeoutRef.current != null) {
          window.clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = window.setTimeout(() => {
          setStatus('saving')
          api.metadata(metadataKey, data).then(
            () => setStatus('user'),
            () => setStatus('error')
          )
        }, 2500)
        const onUnload = () => {
          if (timeoutRef.current) {
            api.metadata(metadataKey, data).catch(() => {})
            window.clearTimeout(timeoutRef.current)
            timeoutRef.current = undefined
          }
        }
        window.addEventListener('beforeunload', onUnload)
        return () => window.removeEventListener('beforeunload', onUnload)
      }
    }
  }, [userId, api, state, metadataKey])

  React.useEffect(() => {
    async function load() {
      if (userId) {
        setStatus('loading')
        const data = await api.metadata(metadataKey).catch(() => null)
        if (!data) {
          setStatus('loaderror')
        } else {
          let state = importData(data, userId)
          if (
            state.profiles.length === 1 &&
            !state.profiles[0].completed.size
          ) {
            state = importData(getStoredValue(metadataKey), userId)
          }
          valueRef.current = state
          setState(state)
          setStatus('user')
        }
      }
    }
    load().catch(() => {})
  }, [userId, api, metadataKey])

  const completed = React.useMemo(
    () =>
      state.profiles[state.active]?.completed ??
      defaultData.profiles[0].completed,
    [state]
  )
  const setCompleted = React.useCallback(
    (value: CallbackWrap<Set<string>>) =>
      setState((state) => {
        if (!state.profiles[state.active]) return state
        state = { ...state }
        state.profiles = state.profiles.slice()
        const profile = (state.profiles[state.active] = {
          ...state.profiles[state.active]
        })
        if (typeof value === 'function') value = value(profile.completed)
        profile.completed = value
        return state
      }),
    []
  )

  const contextValue = React.useMemo(
    () => ({
      state,
      status,
      setState
    }),
    [state, status]
  )

  return [completed, setCompleted, contextValue] as const
}
